import './styles/accessoryArchives.scss';

import { Listing, Pagination } from '../components/Primitives';
import React, { useEffect, useState } from 'react';

import ApplianceAccordion from '../components/appliance/ApplianceAccordion';
import { Breadcrumb } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import PageTitle from '../components/Primitives/PageTitle';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { getCategoryMap } from '@progymedia/sbi-web-englander/src/utils';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($category: String!, $regexCategory: String!, $cmsPage: String!) {
        listing: allProductsJson(filter: { category: { eq: $category } }) {
            nodes {
                code
                id
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                description {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    custom_made {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        categories: allProductsJson(
            filter: { category: { regex: $regexCategory } }
        ) {
            nodes {
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
        listingCms: allProductsJson(filter: { code: { eq: $cmsPage } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function AccessoryArchives({ data, location, pageContext }) {
    const allProducts: any[] = data.listing.nodes;
    const filteredAccessories = allProducts.filter(
        (e) => e?.category === pageContext.category
    );
    const { t } = useTranslation('url');
    const { navigateTranslated, currentLocale } = usePageLocalization();
    const [filteredProducts, setFilteredProducts] =
        useState(filteredAccessories);
    const listingCms = data?.listingCms?.nodes?.[0];
    const pageTitle = listingCms?.title?.[currentLocale] || '';
    const pageDesc = listingCms?.description?.[currentLocale] || '';

    const metadataAttributes = listingCms?.attributes;
    const metadata = {
        title: metadataAttributes?.meta_title?.values[0][currentLocale],
        description:
            metadataAttributes?.meta_description?.values[0][currentLocale],
    };
    const isMobile = useMobileDetect().isMobile();
    const [page, setPage] = useState(1);
    const APPLIANCE_PER_PAGE = isMobile ? 6 : 12;

    const allCategoryMap = getCategoryMap(data.categories.nodes, currentLocale);

    const [typeArg, setTypeArg] = useQueryArg('type');
    const args = typeArg?.split(',');
    const [filterState, setFilterState] = useState({
        categories: typeArg
            ? args.map((arg) => {
                  return { id: arg, slug: `${t('url:accessories')}/${arg}` };
              })
            : null,
    });

    if (location && typeof window !== 'undefined')
        window.scrollTo(0, location?.state?.scrollPosition);

    useEffect(() => {
        const args = typeArg?.split(',');
        if (typeArg) {
            setFilterState({
                categories: args.map((arg) => {
                    return { id: arg, slug: `${t('url:accessories')}/${arg}` };
                }),
            });
        } else {
            setFilterState({ categories: null });
        }
        setPage(1);
    }, [navigateTranslated, setTypeArg, t, typeArg]);

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <Breadcrumb
                className='accessory-listing-breadcrumb'
                elements={[
                    { link: '/', name: t('layout:appliance.home') },
                    {
                        link: t('url:accessories'),
                        name: t('layout:accessory.title'),
                    },
                    { link: '', name: pageTitle },
                ]}
            />
            <div className='accessory-listing-page'>
                <AccessoryFilter
                    allCategoryMap={allCategoryMap}
                    setFilteredProducts={setFilteredProducts}
                    allProducts={allProducts}
                    filterState={filterState}
                    typeArg={typeArg}
                    pageCategory={pageContext.category}
                />
                <div className='listing-container'>
                    <PageTitle title={pageTitle} description={pageDesc} />
                    <Listing
                        items={
                            filteredProducts &&
                            filteredProducts?.slice(
                                APPLIANCE_PER_PAGE * (page - 1),
                                APPLIANCE_PER_PAGE * page
                            )
                        }
                        displayLinkOverride
                        displayManual={false}
                        displayRatings={true}
                        displayPreOrder={true}
                        displayDescription={true}
                        hasOwnPageOverride
                    />
                </div>
            </div>
            {filteredProducts &&
                filteredProducts.length / APPLIANCE_PER_PAGE > 1 && (
                    <Pagination
                        currentPage={page}
                        onPageChange={(newPage: number) => setPage(newPage)}
                        pageNumber={Math.ceil(
                            filteredProducts.length / APPLIANCE_PER_PAGE
                        )}
                    />
                )}
        </Layout>
    );
}
function AccessoryFilter(props: {
    allCategoryMap: Map<any, any>;
    setFilteredProducts: React.Dispatch<any>;
    allProducts: any[];
    filterState: any;
    typeArg: any;
    pageCategory: string;
}) {
    const {
        allCategoryMap,
        allProducts,
        setFilteredProducts,
        filterState,
        typeArg,
        pageCategory,
    } = props;
    const { t } = useTranslation('layout');
    const { currentLocale } = usePageLocalization();
    const { navigateTranslated } = usePageLocalization();
    const [accordionState, setAccordion] = useState(true);
    useEffect(() => {
        const filterCategories = filterState?.categories || [];
        const categories: string[] = filterCategories.map((e) => e?.slug);

        if (filterCategories.length !== 0) {
            const newFilteredProducts = allProducts.filter((product) =>
                categories.find((category) => {
                    const slug: string = product?.slug?.[currentLocale] || '';
                    return slug.toLowerCase().includes(category?.toLowerCase());
                })
            );
            setFilteredProducts(newFilteredProducts);
        } else {
            setFilteredProducts(allProducts);
        }
    }, [
        allProducts,
        currentLocale,
        filterState?.categories,
        setFilteredProducts,
    ]);

    return (
        <div className='accessory-listing-filter'>
            <ApplianceAccordion
                title={t('accessory.title')}
                open={accordionState}
                onClick={() => setAccordion(!accordionState)}
                chevron
            >
                <div className='accessory-filter-category'>
                    <div className='filter-checkbox-group'>
                        {Array.from(allCategoryMap)[0][1]
                            .sort((a, b) => {
                                if (a.label < b.label) return -1;
                                if (a.label > b.label) return 1;
                                return 0;
                            })
                            .map((single, index) => {
                                return getCategoryOption(
                                    single.name,
                                    single.label,
                                    index,
                                    single.slug,
                                    typeArg,
                                    single.category,
                                    pageCategory,
                                    navigateTranslated
                                );
                            })}
                    </div>
                </div>
            </ApplianceAccordion>
        </div>
    );

    function getCategoryOption(
        id: string,
        label: string[],
        index: number,
        value: string,
        typeArg: any,
        category: string,
        pageCategory: string,
        navigateTranslated
    ) {
        const isChecked = pageCategory === category;

        return (
            <div className='checkbox-and-label' key={index}>
                <input
                    id={id}
                    type='checkbox'
                    checked={isChecked}
                    onChange={(event) => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;

                        if (event.currentTarget.checked) {
                            navigateTranslated(value + '/', scrollPosition);
                        }
                    }}
                />
                <label
                    htmlFor={id}
                    className={`checkbox-label ${isChecked ? 'bold' : ''}`}
                >
                    {label}
                </label>
            </div>
        );
    }
}
